import React from "react";
import './common.css';

export default function SaralModeration() {
  return (
    <div className="container">
      <header className="header">
        <h1 className="logo">Saral Platform</h1>
        <nav className="nav">
          <a href="/">Home</a>
          <a href="/SaralContact">CCaaS</a>
          <a href="/SaralTranslate">Translation</a>
        </nav>
      </header>

      <section className="intro" style={{
        backgroundImage: 'linear-gradient(rgba(102, 126, 234, 0.85), rgba(118, 75, 162, 0.85)), url(https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        <h2 className="intro-tagline">Content Moderation</h2>
        <p className="intro-subtitle">Smart content moderation protecting brands, users, and platforms</p>
      </section>

      <section className="features">
        <h3>What is Content Moderation?</h3>
        <div style={{maxWidth: '900px', margin: '0 auto', textAlign: 'left', padding: '0 2rem'}}>
          <p style={{fontSize: '1.3rem', marginBottom: '2rem', fontWeight: '500'}}>
            Content moderation is the process of monitoring and reviewing user-generated content to ensure it meets community guidelines, legal requirements, and brand safety standards. Our AI-powered solution automatically detects and filters inappropriate content across text, images, audio, and video.
          </p>
        </div>
      </section>

      <section className="features">
        <h3>Multi-Format Content Moderation</h3>
        <div className="feature-list">
          <div className="feature">
            <div className="feature-icon">📝</div>
            <h4>Text Moderation</h4>
            <p>Detect profanity, hate speech, harassment, personal information (PII), and toxic language in real-time.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🖼️</div>
            <h4>Image Moderation</h4>
            <p>Identify inappropriate images, explicit content, violence, weapons, and brand safety violations.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🎵</div>
            <h4>Audio Moderation</h4>
            <p>Analyze audio content for inappropriate language, hate speech, and policy violations.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🎥</div>
            <h4>Video Moderation</h4>
            <p>Scan video content frame-by-frame for inappropriate visuals, audio, and text overlays.</p>
          </div>
        </div>
      </section>

      <section className="features">
        <h3>Key Features</h3>
        <div style={{maxWidth: '900px', margin: '0 auto', textAlign: 'left', padding: '0 2rem'}}>
          <ul style={{fontSize: '1.1rem', lineHeight: '2', listStyle: 'disc', paddingLeft: '2rem'}}>
            <li><strong>Real-Time Detection:</strong> Instant analysis and filtering of content as it's uploaded or posted.</li>
            <li><strong>Customizable Policies:</strong> Define your own moderation rules and sensitivity levels based on your brand guidelines.</li>
            <li><strong>Multi-Language Support:</strong> Detect inappropriate content in 100+ languages.</li>
            <li><strong>Confidence Scores:</strong> Get detailed confidence scores for each detection to fine-tune your moderation workflow.</li>
            <li><strong>Human Review Integration:</strong> Seamlessly escalate edge cases to human moderators for final review.</li>
            <li><strong>Compliance Ready:</strong> Meet regulatory requirements including COPPA, GDPR, and platform-specific guidelines.</li>
            <li><strong>API-First Design:</strong> Simple REST APIs for easy integration into your existing applications.</li>
            <li><strong>Scalable Infrastructure:</strong> Handle millions of content items per day with low latency.</li>
          </ul>
        </div>
      </section>

      <section className="features">
        <h3>What We Detect</h3>
        <div className="feature-list">
          <div className="feature">
            <div className="feature-icon">🚫</div>
            <h4>Explicit Content</h4>
            <p>Nudity, sexual content, and adult material across all media types.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">⚠️</div>
            <h4>Violence & Gore</h4>
            <p>Graphic violence, weapons, blood, and disturbing imagery.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">💬</div>
            <h4>Hate Speech & Harassment</h4>
            <p>Discriminatory language, bullying, threats, and toxic behavior.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🔒</div>
            <h4>Personal Information</h4>
            <p>PII detection including phone numbers, addresses, credit cards, and social security numbers.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">💊</div>
            <h4>Drugs & Alcohol</h4>
            <p>Drug use, alcohol abuse, and substance-related content.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🎰</div>
            <h4>Gambling & Illegal Activities</h4>
            <p>Gambling content, illegal activities, and fraudulent schemes.</p>
          </div>
        </div>
      </section>

      <section className="features">
        <h3>Simple API Integration</h3>
        <div style={{maxWidth: '900px', margin: '0 auto', textAlign: 'left', padding: '0 2rem'}}>
          <p style={{fontSize: '1.3rem', marginBottom: '1.5rem', fontWeight: '500'}}>
            Get started in minutes with our simple REST APIs:
          </p>
          <ul style={{fontSize: '1.1rem', lineHeight: '2', listStyle: 'disc', paddingLeft: '2rem'}}>
            <li><strong>Single Endpoint:</strong> One API endpoint for all content types (text, image, audio, video).</li>
            <li><strong>Webhook Support:</strong> Receive real-time notifications for moderation results.</li>
            <li><strong>Batch Processing:</strong> Process multiple items in a single API call for efficiency.</li>
            <li><strong>SDKs Available:</strong> Pre-built SDKs for Python, Node.js, Java, PHP, and more.</li>
            <li><strong>Comprehensive Documentation:</strong> Clear API docs with code examples and tutorials.</li>
            <li><strong>Sandbox Environment:</strong> Test your integration risk-free before going live.</li>
          </ul>
        </div>
      </section>

      <section className="features">
        <h3>Use Cases</h3>
        <div className="feature-list">
          <div className="feature">
            <div className="feature-icon">💬</div>
            <h4>Social Media Platforms</h4>
            <p>Protect your community from harmful content and maintain a safe environment.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🛒</div>
            <h4>E-Commerce Marketplaces</h4>
            <p>Ensure product listings, reviews, and seller content meet your standards.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🎮</div>
            <h4>Gaming Platforms</h4>
            <p>Monitor in-game chat, user profiles, and uploaded content for policy violations.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">📱</div>
            <h4>Dating Apps</h4>
            <p>Screen profiles, messages, and photos to create a safe dating experience.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🎓</div>
            <h4>Educational Platforms</h4>
            <p>Protect students with age-appropriate content filtering and safety measures.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">📺</div>
            <h4>Video Streaming Services</h4>
            <p>Moderate user-uploaded videos, comments, and live streams at scale.</p>
          </div>
        </div>
      </section>

      <section id="contact" className="contact">
        <h3>Ready to Protect Your Platform?</h3>
        <p>Let's discuss how Saral Platform can help you maintain a safe and trusted community.</p>
        <p>Email us at <a href="mailto:mandar@saralplatform.com">mandar@saralplatform.com</a></p>
        <p>Call us at <a href="tel:770-378-8627">770-378-8627</a></p>
      </section>

      <footer className="footer">
        &copy; 2025 Saral Platform. All rights reserved.
      </footer>
    </div>
  );
}
